/**
 * @file cardRemoval.ts
 * @brief What removing a saved card does: promote another to default, or warn.
 *
 * @details
 * The backend detaches the card and leaves the customer with no default when
 * the default goes (payments audit fix #19), and every hold then fails with
 * MISSING_PAYMENT_METHOD although other cards are saved. So the page promotes
 * the next card itself (newest first, the order Stripe lists them) right after
 * the delete. Removing the last card is allowed but warned, louder when a plan
 * will try to renew on it.
 *
 * Pure (relative imports only) so node:test can load it.
 *
 * @author DES
 * @date 17-10-2026
 */

export type CardLike = { id: string; brand: string; last4: string };

export type RemovalPlan = {
  /** The card to make default after the delete, if any. */
  promote: CardLike | null;
  /** Shown in the confirm dialog. */
  warning: string | null;
};

export function planCardRemoval(
  cards: CardLike[],
  defaultId: string | null | undefined,
  removeId: string,
  opts: { planRenews?: string | null } = {},
): RemovalPlan {
  const others = cards.filter((c) => c.id !== removeId);
  const removingDefault = removeId === defaultId || !defaultId;
  if (others.length === 0) {
    return {
      promote: null,
      warning: opts.planRenews
        ? `This is your only card. Your plan renews on ${opts.planRenews} and the payment will fail without a card. Recordings and sessions also need a card to start.`
        : "This is your only card. Recordings and sessions need a card to start, so add another before your next one.",
    };
  }
  if (!removingDefault) return { promote: null, warning: null };
  const next = others[0]!;
  return {
    promote: next,
    warning: `${brandName(next.brand)} •••• ${next.last4} will become your default card.`,
  };
}

export function brandName(brand: string): string {
  const b = (brand || "").trim();
  if (!b) return "Card";
  if (b.toLowerCase() === "amex") return "American Express";
  return b.charAt(0).toUpperCase() + b.slice(1);
}
